/**
 * PausedModal Component
 * Shows when quiz is paused due to a team captain disconnecting
 * Displays countdown timer (2 minutes) and waiting message
 */

import React from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

interface PausedModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Name of the player we're waiting for */
  playerName: string;
  /** Time remaining until timeout (seconds) */
  timeRemaining: number;
  /** Whether the viewer is an admin */
  isAdmin?: boolean;
  /** Whether the paused player appears to be back online (fresh last_seen) */
  isPlayerBack?: boolean;
  /** Called when admin wants to reassign captain */
  onReassignCaptain?: () => void;
  /** Called when admin resumes the quiz */
  onResume?: () => void;
  /** Called when admin cancels the quiz */
  onCancelQuiz?: () => void;
}

/**
 * Format seconds to MM:SS
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * PausedModal - Displayed when quiz is paused waiting for reconnection
 */
export const PausedModal: React.FC<PausedModalProps> = ({
  visible,
  playerName,
  timeRemaining,
  isAdmin = false,
  isPlayerBack = false,
  onReassignCaptain,
  onResume,
  onCancelQuiz,
}) => {
  const colors = useThemeColors();
  
  // Show different UI when timeout is imminent
  const isTimeoutSoon = timeRemaining <= 30;
  
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
    >
      <View className="flex-1 items-center justify-center bg-black/80 px-6">
        <View 
          className="w-full max-w-md rounded-3xl p-8 items-center"
          style={{ backgroundColor: colors.surface }}
        >
          {/* Pause Icon/Animation */}
          <View 
            className="w-24 h-24 rounded-full items-center justify-center mb-4"
            style={{ backgroundColor: "#F59E0B20" }}
          >
            <Ionicons name="pause-circle" size={60} color="#F59E0B" />
          </View>
          
          {/* Title */}
          <Text 
            className="text-2xl font-bold text-center mb-2"
            style={{ color: colors.text }}
          >
            ⏸️ Quiz Gepauzeerd
          </Text>
          
          {/* Message */}
          <Text 
            className="text-center text-base mb-4"
            style={{ color: colors.textSecondary }}
          >
            We wachten tot{" "}
            <Text style={{ fontWeight: "600", color: colors.text }}>
              {playerName}
            </Text>
            {" "}weer verbonden is.
          </Text>
          
          {/* Countdown Timer */}
          <View 
            className="px-6 py-4 rounded-2xl items-center mb-4"
            style={{ 
              backgroundColor: isTimeoutSoon ? "#EF444420" : colors.surfaceSecondary,
            }}
          >
            <Text 
              className="text-xs uppercase tracking-wider mb-1"
              style={{ color: colors.textTertiary }}
            >
              Tijd om opnieuw te verbinden
            </Text>
            <Text 
              className="text-4xl font-bold"
              style={{ color: isTimeoutSoon ? "#EF4444" : colors.primary }}
            >
              {formatTime(timeRemaining)}
            </Text>
          </View>
          
          {/* Info message */}
          <View 
            className="flex-row items-center rounded-xl px-4 py-3 mb-6"
            style={{ backgroundColor: colors.primary + "10" }}
          >
            <Ionicons 
              name="information-circle" 
              size={20} 
              color={colors.primary} 
            />
            <Text 
              className="flex-1 ml-2 text-sm"
              style={{ color: colors.text }}
            >
              De timer is gestopt. Het spel gaat verder zodra iedereen weer verbonden is.
            </Text>
          </View>
          
          {/* Admin Actions */}
          {isAdmin && (
            <View className="w-full gap-3">
              {/* Resume when player is back online */}
              {isPlayerBack && onResume && (
                <TouchableOpacity
                  onPress={onResume}
                  className="w-full py-4 rounded-xl items-center"
                  style={{ backgroundColor: "#10B981" }}
                  accessibilityLabel="Quiz hervatten"
                  accessibilityRole="button"
                >
                  <View className="flex-row items-center">
                    <Ionicons name="play" size={20} color="white" />
                    <Text className="ml-2 font-semibold text-white">
                      Speler is terug — Hervat Quiz
                    </Text>
                  </View>
                </TouchableOpacity>
              )}

              {/* Reassign Captain — always available when paused */}
              {onReassignCaptain && (
                <TouchableOpacity
                  onPress={onReassignCaptain}
                  className="w-full py-4 rounded-xl items-center"
                  style={{ backgroundColor: colors.primary }}
                  accessibilityLabel="Wijs nieuwe teamkapitein aan"
                  accessibilityRole="button"
                >
                  <View className="flex-row items-center">
                    <Ionicons name="people" size={20} color="white" />
                    <Text className="ml-2 font-semibold text-white">
                      Wijs Nieuw Teamlid Aan
                    </Text>
                  </View>
                </TouchableOpacity>
              )}
              
              {/* Cancel Quiz */}
              {onCancelQuiz && (
                <TouchableOpacity
                  onPress={onCancelQuiz}
                  className="w-full py-3 rounded-xl items-center"
                  style={{ backgroundColor: "#EF444420" }}
                  accessibilityLabel="Quiz annuleren"
                  accessibilityRole="button"
                >
                  <Text style={{ color: "#EF4444", fontWeight: "600" }}>
                    Quiz Annuleren
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
          
          {/* Non-admin waiting message */}
          {!isAdmin && (
            <View className="flex-row items-center opacity-60">
              <ActivityIndicator size="small" color={colors.textSecondary} />
              <Text 
                className="ml-2 text-sm"
                style={{ color: colors.textSecondary }}
              >
                Even geduld...
              </Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

export default PausedModal;

